/**
 * Fine-grained permissions. Roles are admin-editable bundles of these
 * (ADR-0005). Permission strings are stable — they are stored in the DB.
 */
export const PERMISSIONS = {
  // Surveys
  'survey.view': 'View surveys and their answers',
  'survey.create': 'Start surveys and fill them in',
  'survey.takeover': "Take over another reporter's draft",
  'survey.review': 'Confirm or correct controls, return surveys to the reporter',
  'survey.communicate': 'Send survey results to the customer',
  'survey.cancel': 'Cancel a survey',
  'survey.schedule': 'Enrol sites, change frequency, trigger unscheduled surveys',

  // Findings
  'finding.view': 'View findings',
  'finding.manage': 'Agree action plans and record resolutions',
  'finding.close': 'Verify and close findings',

  // Event reports: hazards, incidents, conflict scenarios (ADR-0029)
  'event.report': 'Report hazards and incidents',
  'event.view': 'View event reports (classification, dates and status)',
  'event.view_detail': 'View event narratives, people and attachments',
  'event.triage': 'Triage event reports, plan actions and route investigations',
  'event.close': 'Close event reports',

  // Templates
  'template.view': 'View templates',
  'template.design': 'Create and edit draft templates',
  'template.publish': 'Publish template versions',

  // Master data
  'masterdata.view': 'View customers, sites and branches',
  'masterdata.edit': 'Edit customers, sites and branches',
  'masterdata.import': 'Import customers and sites from CSV',

  // Administration
  'users.view': 'View users and their roles',
  'users.manage': 'Invite users, assign roles, disable accounts',
  'roles.manage': 'Create and edit roles',
  'settings.manage': 'Change settings, branding and legal texts',
  'api.keys.manage': 'Create and revoke API clients',

  // Reporting, safety & audit
  'reports.view': 'View dashboards and operational reports',
  'safety.command': 'Access the Zero-Harm Command center and site safety posture',
  'insights.view': 'View aggregate insights (anonymised above the identification level, ADR-0030)',
  'intel.manage': 'Import and remove external security intelligence',
  'catalogue.manage': 'Manage the solution catalogue and which solution answers each control',
  'data.export': 'Export lists to CSV/Excel',
  'audit.view': 'Search the audit log',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export function isPermission(p: string): p is Permission {
  return p in PERMISSIONS;
}
